import { generateMacroBundleMessage, MacroBundleMessageType, macroBundleResponseSchema,
  type MacroBundleError, type MacroExecutionBundle, type MacroTrigger, type PlaybackAssetGrant } from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../realtime/IRealtimeService';
import type { IMessageRouter } from '../MessageRouter';

export type MacroBundleLoadError = MacroBundleError | 'timeout' | 'invalid_response';
/** The watcher path keeps its original two-arm result: only `launch` can be disabled. */
export type MacroBundleLoad =
  | { bundle: MacroExecutionBundle; assets: PlaybackAssetGrant }
  | { error: MacroBundleLoadError };
/**
 * The launch path adds the disabled arm. `no_macro` is a SUCCESS: the campaign
 * has no macro to run, so Host completes the launch without any native call.
 */
export type MacroBundleLoadResult =
  | { kind: 'bundle'; bundle: MacroExecutionBundle; assets: PlaybackAssetGrant }
  | { kind: 'no_macro' }
  | { kind: 'error'; error: MacroBundleLoadError };

const BUNDLE_REPLY_TIMEOUT_MS = 15_000;

interface PendingBundleRequest {
  id: string;
  campaignId: string;
  trigger: MacroTrigger;
  resolve(result: MacroBundleLoadResult): void;
  timer: ReturnType<typeof setTimeout>;
}

/** One correlated `MACROBUNDLE_REQUEST` at a time over the host's already-authenticated socket. */
export class MacroBundleSocketLoader {
  private pending?: PendingBundleRequest;

  public constructor(private readonly realtime: IRealtimeService, router: IMessageRouter) {
    realtime.onConnected(() => this.cancel());
    router.register(MacroBundleMessageType.RESPONSE, message => this.receive(message));
  }

  /** Settles any in-flight request as `unavailable`; a later `load` starts fresh. */
  public cancel(): void {
    this.settle({ kind: 'error', error: 'unavailable' });
  }

  /** Watcher checks keep the original two-arm result. */
  public load(campaignId: string, trigger: 'watcher'): Promise<MacroBundleLoad>;
  /** The launch path can succeed with `no_macro` for a disabled revision. */
  public load(campaignId: string, trigger: 'launch'): Promise<MacroBundleLoadResult>;
  public load(campaignId: string, trigger: MacroTrigger): Promise<MacroBundleLoad | MacroBundleLoadResult>;
  public load(campaignId: string, trigger: MacroTrigger): Promise<MacroBundleLoad | MacroBundleLoadResult> {
    if (this.pending) return Promise.resolve(trigger === 'watcher' ? { error: 'busy' } : { kind: 'error', error: 'busy' });
    if (!this.realtime.isConnected()) {
      return Promise.resolve(trigger === 'watcher' ? { error: 'unavailable' } : { kind: 'error', error: 'unavailable' });
    }
    return new Promise<MacroBundleLoadResult>(resolve => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => {
        if (this.pending?.id === id) this.settle({ kind: 'error', error: 'timeout' });
      }, BUNDLE_REPLY_TIMEOUT_MS);
      this.pending = { id, campaignId, trigger, resolve, timer };
      try {
        this.realtime.sendMessage(generateMacroBundleMessage({
          type: MacroBundleMessageType.REQUEST, requestId: id, campaignId, trigger,
        }));
      } catch {
        this.settle({ kind: 'error', error: 'unavailable' });
      }
    }).then(result => trigger === 'watcher' ? watcherShape(result) : result);
  }

  private receive(message: unknown): void {
    const pending = this.pending;
    if (!pending) return;
    const envelope = message as { requestId?: unknown; campaignId?: unknown };
    if (envelope.requestId !== pending.id || envelope.campaignId !== pending.campaignId) return;
    const parsed = macroBundleResponseSchema.safeParse(message);
    if (!parsed.success) return this.settle({ kind: 'error', error: 'invalid_response' });
    if ('error' in parsed.data) return this.settle({ kind: 'error', error: parsed.data.error });
    if ('outcome' in parsed.data) return this.settle({ kind: 'no_macro' });
    const { bundle, assets } = parsed.data;
    if (bundle.campaignId !== pending.campaignId || bundle.trigger !== pending.trigger) {
      return this.settle({ kind: 'error', error: 'invalid_response' });
    }
    this.settle({ kind: 'bundle', bundle, assets });
  }

  private settle(result: MacroBundleLoadResult): void {
    const pending = this.pending;
    if (!pending) return;
    this.pending = undefined;
    clearTimeout(pending.timer);
    pending.resolve(result);
  }
}

/** Collapses the launch result back to the watcher contract, where `no_macro` is invalid. */
function watcherShape(result: MacroBundleLoadResult): MacroBundleLoad {
  if (result.kind === 'bundle') return { bundle: result.bundle, assets: result.assets };
  if (result.kind === 'no_macro') return { error: 'invalid_response' };
  return { error: result.error };
}
